/**
 * Implements infinite scrolling functionality for a web component.
 *  This component loads additional content automatically as the user scrolls to the bottom of the page.
 *  It requires an element with class `.js-pagination__next-button` for fetching the next set of items.
 *
 * Usage:
 *  {%- paginate blog.articles by section.settings.items_per_page -%}
 *    <infinite-scroll data-infinite-scroll-enabled="{% if settings.pagination_style == 'infinite' %}true{% else %}false{% endif %}">
 *      <div class="js-pagination__item">One</div>
 *      <div class="js-pagination__item">Two</div>
 *      <div class="js-pagination__item">Three</div>
 *
 *      {%- if paginate.pages > 1 -%}
 *        {% render 'pagination', paginate: paginate %}
 *      {%- endif -%}
 *    </infinite-scroll>
 *  {%- endpaginate -%}
 */
/*
 * WOEST CUSTOM: "Toon meer" onthouden (zie WOEST-CUSTOM.md).
 * Na "Toon meer" bewaren we de bijgeladen producten, de volgende-pagina-link en de scrollpositie
 * in sessionStorage (per URL, incl. filters). Gaat de bezoeker vanaf een product terug, dan zetten
 * we die producten en de positie terug, in plaats van opnieuw bij de eerste producten te beginnen.
 */
if (!customElements.get('infinite-scroll')) {
  const WOEST_PG_PREFIX = 'woest-pg:';
  const WOEST_PG_MAX_AGE = 30 * 60 * 1000;
  // Shared between instances: only the first list after a page load may restore
  const woestPgFlags = { restoreChecked: false };

  function woestPgKey() {
    return WOEST_PG_PREFIX + window.location.pathname + window.location.search;
  }

  function woestPgRead() {
    try {
      const raw = window.sessionStorage.getItem(woestPgKey());
      if (!raw) return null;
      const state = JSON.parse(raw);
      if (!state || !state.html || Date.now() - state.ts > WOEST_PG_MAX_AGE) return null;
      return state;
    } catch (error) {
      return null;
    }
  }

  // Houd hooguit 2 andere lijsten bij (en niets ouder dan 30 min), zodat sessionStorage niet volloopt
  function woestPgPrune(keepKey, keepCount) {
    try {
      const store = window.sessionStorage;
      const entries = [];
      for (let i = 0; i < store.length; i++) {
        const key = store.key(i);
        if (!key || key.indexOf(WOEST_PG_PREFIX) !== 0 || key === keepKey) continue;
        const match = (store.getItem(key) || '').slice(0, 40).match(/"ts":(\d+)/);
        entries.push({ key, ts: match ? Number(match[1]) : 0 });
      }
      entries.sort((a, b) => b.ts - a.ts);
      entries.forEach((entry, index) => {
        if (index >= keepCount || Date.now() - entry.ts > WOEST_PG_MAX_AGE) store.removeItem(entry.key);
      });
    } catch (error) {
      /* sessionStorage niet beschikbaar */
    }
  }

  function woestPgWrite(state) {
    const key = woestPgKey();
    const value = JSON.stringify(state);
    woestPgPrune(key, 2);
    try {
      window.sessionStorage.setItem(key, value);
      return true;
    } catch (error) {
      // Opslag vol: alle andere lijsten weg en nog één keer proberen
      woestPgPrune(key, 0);
      try {
        window.sessionStorage.setItem(key, value);
        return true;
      } catch (retryError) {
        return false;
      }
    }
  }

  function woestPgClear() {
    try {
      window.sessionStorage.removeItem(woestPgKey());
    } catch (error) {
      /* sessionStorage niet beschikbaar */
    }
  }

  function woestPgIsBackForward() {
    try {
      const nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
      return !!nav && nav.type === 'back_forward';
    } catch (error) {
      return false;
    }
  }

  class InfiniteScroll extends HTMLElement {
    constructor() {
      super();
      this.init();
    }

    // Cleans up resources when the element is removed from the DOM
    disconnectedCallback() {
      this.destroy();
    }

    init() {
      // Elements involved in pagination
      this.paginationContainer = this.querySelector('.js-pagination');
      this.paginationItems = this.querySelectorAll('.js-pagination__item');
      this.nextButton = this.querySelector('.js-pagination__next-button');
      this.appendedHTML = '';
      this.lastPageReached = false;

      // Guard clause to ensure necessary elements exist
      if (!this.paginationContainer || !this.paginationItems.length || !this.nextButton || !this.nextButton.hasAttribute('href')) {
        return;
      }

      this.firstNextHref = this.nextButton.getAttribute('href');
      this.restoreState();
      this.bindStateListeners();
      if (this.lastPageReached) return;

      this.bindListeners();
    }

    // Removes event listeners and stops any ongoing intervals or observers
    destroy() {
      clearInterval(this.nextButtonTimer);
      this.nextButton?.removeEventListener('click', this.clickHandler);
      this.intersectionObserver?.disconnect();
      if (this.pageHideHandler) window.removeEventListener('pagehide', this.pageHideHandler);
      if (this.linkClickHandler) this.removeEventListener('click', this.linkClickHandler, true);
    }

    // WOEST CUSTOM: puts back the items loaded with "Toon meer" when the visitor returns with the back button
    restoreState() {
      // Only the first list on a page load may restore (not after filtering, which re-renders this element)
      if (woestPgFlags.restoreChecked) return;
      woestPgFlags.restoreChecked = true;

      if (!woestPgIsBackForward()) {
        woestPgClear();
        return;
      }

      const state = woestPgRead();
      if (!state || state.firstNextHref !== this.firstNextHref) return;

      this.paginationItems[this.paginationItems.length - 1].insertAdjacentHTML('afterend', state.html);
      this.paginationItems = this.querySelectorAll('.js-pagination__item');
      this.appendedHTML = state.html;

      if (state.nextHref) {
        this.nextButton.setAttribute('href', state.nextHref);
      } else {
        this.cleanupAfterLastPage();
      }

      setTimeout(() => {
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger();
        window.renderSelects?.();
        window.loadDesktopOnlyTemplates?.();
        window.stickersReinit?.();
      }, 300);

      if (typeof state.scrollY === 'number' && state.scrollY > 0) {
        if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
        const scrollBack = () => window.scrollTo(0, state.scrollY);
        requestAnimationFrame(scrollBack);
        setTimeout(scrollBack, 150);
        setTimeout(scrollBack, 600);
      }
    }

    // WOEST CUSTOM: stores the loaded items, the next-page link and the scroll position for this URL
    saveState() {
      if (!this.appendedHTML) return;
      const saved = woestPgWrite({
        ts: Date.now(),
        html: this.appendedHTML,
        firstNextHref: this.firstNextHref,
        nextHref: this.lastPageReached ? null : this.nextButton?.getAttribute('href') || null,
        scrollY: Math.round(window.scrollY),
      });
      // Let the script restore the position instead of the browser (the browser would stop at the first items)
      if (saved && 'scrollRestoration' in history) history.scrollRestoration = 'manual';
    }

    bindStateListeners() {
      this.pageHideHandler = () => this.saveState();
      window.addEventListener('pagehide', this.pageHideHandler);
      this.linkClickHandler = event => {
        if (event.target.closest('a[href]:not(.js-pagination__next-button)')) this.saveState();
      };
      this.addEventListener('click', this.linkClickHandler, true);
    }

    // Handles the "Next" button click to fetch the next set of items
    onNextButtonClick(event) {
      event.preventDefault();
      this.fetchNextItems();
    }

    // Sets up the infinite scroll functionality
    bindListeners() {
      // Setup click handler for the "Next" button
      this.clickHandler = this.onNextButtonClick.bind(this);
      this.nextButton.addEventListener('click', this.clickHandler);

      // Initialize IntersectionObserver if infinite scrolling is enabled
      if (this.dataset.infiniteScrollEnabled === 'true') {
        this.intersectionObserver = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              // Load more results when the "Next" button is in view
              this.fetchNextItems();
              this.nextButtonTimer = setInterval(this.fetchNextItems.bind(this), 1500);
            } else {
              clearInterval(this.nextButtonTimer);
            }
          });
        }, {rootMargin: `${window.innerHeight}px 0px`});

        this.intersectionObserver.observe(this.paginationContainer);
      }
    }

    // Fetches the next set of items and updates the DOM
    async fetchNextItems() {
      if (this.fetchInProgress) return;

      let originalButtonText = this.nextButton.textContent;
      this.fetchInProgress = true;
      this.nextButton.setAttribute('aria-disabled', 'true');
      this.nextButton.textContent = window.strings.general.pagination.loadingText;

      try {
        const response = await fetch(this.nextButton.href);
        if (!response.ok) throw new Error(`Failed to fetch: ${response.statusText}`);

        const template = document.createElement('template');
        template.innerHTML = await response.text();

        // Append new items
        const newItemsHTML = Array.from(template.content.querySelectorAll('.js-pagination__item'))
          .map(item => item.outerHTML)
          .join('');
        this.paginationItems[this.paginationItems.length - 1].insertAdjacentHTML('afterend', newItemsHTML);
        this.appendedHTML += newItemsHTML;
        const lastIndex = this.paginationItems.length
        this.paginationItems = this.querySelectorAll('.js-pagination__item');

        setTimeout(() => {
          if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger();
          window.renderSelects();
          window.loadDesktopOnlyTemplates();
          window.stickersReinit?.();

          if (document.body.classList.contains('tab-active') && window.theme?.settings.paginationLoadType === 'show_more') {
            this.paginationItems[lastIndex]?.querySelector('a')?.focus();
          }
        }, 300);

        // Update pagination controls
        this.updatePaginationControls(template);
        this.saveState();
      } catch (error) {
        console.error('Failed to load new items:', error);
      } finally {
        this.fetchInProgress = false;
        this.nextButton.removeAttribute('aria-disabled');
        this.nextButton.textContent = originalButtonText;
      }
    }

    // Updates pagination controls based on the fetched content
    updatePaginationControls(template) {
      const nextPagination = template.content.querySelector('.js-pagination');
      if (nextPagination && nextPagination.querySelector('.js-pagination__next-button[href]')) {
        this.paginationContainer.innerHTML = nextPagination.innerHTML;
        this.refreshNextButton();
      } else {
        this.cleanupAfterLastPage();
      }
    }

    // Refreshes the "Next" button event listener after content update
    refreshNextButton() {
      this.nextButton.removeEventListener('click', this.clickHandler);
      this.nextButton = this.querySelector('.js-pagination__next-button');
      this.nextButton.addEventListener('click', this.clickHandler);
    }

    // Removes pagination and related elements when no more pages are available
    cleanupAfterLastPage() {
      this.lastPageReached = true;
      if (this.intersectionObserver) this.intersectionObserver.disconnect();
      clearInterval(this.nextButtonTimer);
      this.paginationContainer.remove();
      document.querySelectorAll('.js-when-paginated-only').forEach(elem => elem.remove());
    }
  }

  customElements.define('infinite-scroll', InfiniteScroll);
}